/**
 * Shapes and resolution shared by both CI queue row builders.
 *
 * Both tabs answer the same three questions about a row — who is the person in
 * this people-column, was this send automated, and is this lead active — and
 * both read them out of the same GraphQL selections. Keeping the answers here
 * means a change to any of them cannot land on one tab and miss the other.
 */

import { Appointment, Communication, Flag, HumanName, Patient, Practitioner, Task } from '@medplum/fhirtypes';
import {
  ActiveLeadAppointment,
  ActiveLeadState,
  deriveActiveLead,
  parseActiveLeadOverride,
} from '@bariendo/lib/active-lead';
import { getClinicalTaskDueDate } from '@bariendo/lib/clinical-tasks';
import { getPayloadByType } from '@bariendo/lib/communication';
import { CommunicationCategory, TEXT_CONTENT_PAYLOAD_ID, Track, TrackLabel } from '@bariendo/lib/constants';
import { formatGivenFamilyName } from '@bariendo/lib/human-name';
import { appointmentHasOccurred } from '@bariendo/lib/pac-list-appointments';
import { PaymentStatusResult } from '@bariendo/lib/payment-status';
import { getNextTask } from '@bariendo/lib/pac-post-consultation-list';
import { isPnConsultationAppointment } from '@bariendo/lib/patient-access';
import { isServiceAccount } from '@bariendo/lib/profile';
import type { PacQueueHydration } from '../hooks/usePacQueueHydration';

/** A `Practitioner` as selected by the queries' single union branch. */
interface ResolvedPractitioner {
  resourceType: 'Practitioner';
  id?: string;
  name?: HumanName[];
  /** Present on a hydrated sender only; the queries' union branch does not select it. */
  identifier?: Practitioner['identifier'];
}

interface ResolvedActor {
  display?: string;
  reference?: string;
  resource?: { resourceType?: string; name?: HumanName[] };
}

export interface QueryParticipant {
  actor?: ResolvedActor;
}

/**
 * A GraphQL selection returns only the fields it asked for, but
 * `@medplum/fhirtypes` marks `Appointment.status` and `.participant` required.
 * Modelling query output as a `Partial` keeps the fixtures honest — a test that
 * had to invent a `status` to satisfy the compiler would be asserting against a
 * shape the server never sends.
 */
type AppointmentSelection = Partial<Omit<Appointment, 'resourceType' | 'participant'>> & {
  resourceType: 'Appointment';
};

export interface ConsultAppointmentResult extends AppointmentSelection {
  participant?: QueryParticipant[];
}

/**
 * A root row of a PAC queue query: an intro call, or — since Intake 1.1 — the
 * consult a lead self-scheduled instead of one.
 */
export interface PacQueueAppointmentResult extends AppointmentSelection {
  introCallNote?: Communication[];
  participant?: QueryParticipant[];
}

export interface LastCommunicationResult extends Communication {
  sender?: Communication['sender'] & { resource?: { resourceType?: string; name?: HumanName[] } };
}

/**
 * The patient as the queue queries select it (`QueuePatientFragment`). Everything
 * that used to hang off it as a nested reverse-chain — Last communication
 * included — now arrives through bulk hydration.
 */
export type PreConsultationPatientResult = Patient;

export interface NextTaskCell {
  dueDate: string;
  title?: string;
  /** Absent when the task sits in a role pool rather than with a person. */
  ownerName?: HumanName;
}

/** The soonest dated open task, with its assignee resolved through hydration. */
export function toNextTaskCell(
  tasks: Task[] | undefined,
  practitionerByRef: Map<string, Practitioner>
): NextTaskCell | undefined {
  const task = getNextTask(tasks);
  const dueDate = task && getClinicalTaskDueDate(task);

  if (!task || !dueDate) {
    return undefined;
  }

  const owner = task.owner?.reference;

  return {
    dueDate,
    title: task.description,
    ownerName: owner ? practitionerByRef.get(owner)?.name?.[0] : undefined,
  };
}

export interface LastCommunicationCell {
  sent?: string;
  /** Absent for an automated send; `systemLabel` carries it instead. */
  name?: HumanName;
  systemLabel?: string;
  /** Contact attempts, on the chasing tabs only (Inquiry, No-show). */
  attempts?: number;
}

function isPractitioner(resource: ResolvedActor['resource']): resource is ResolvedPractitioner {
  return resource?.resourceType === 'Practitioner';
}

export function firstPractitionerName(participants?: QueryParticipant[]): HumanName | undefined {
  for (const participant of participants ?? []) {
    const resource = participant.actor?.resource;
    if (isPractitioner(resource)) {
      return resource.name?.[0];
    }
  }
  return undefined;
}

export function patientParticipant(participants?: QueryParticipant[]): PreConsultationPatientResult | undefined {
  for (const participant of participants ?? []) {
    const resource = participant.actor?.resource;
    if (resource?.resourceType === 'Patient') {
      return resource as PreConsultationPatientResult;
    }
  }
  return undefined;
}

const AUTOMATED_LABEL_BY_CATEGORY: Record<string, string> = {
  [CommunicationCategory.SMS_MESSAGE]: 'SMS',
  [CommunicationCategory.CHAT]: 'Chat',
  [CommunicationCategory.PHONE_CALL]: 'Call',
};

function automatedLabel(communication: Communication): string {
  for (const category of communication.category ?? []) {
    for (const coding of category.coding ?? []) {
      const label = coding.code && AUTOMATED_LABEL_BY_CATEGORY[coding.code];
      if (label) {
        return `Automated ${label}`;
      }
    }
  }
  return 'Automated message';
}

/**
 * A send with no resolvable Practitioner sender is automated, and so is one
 * whose sender is a service account.
 *
 * Deliberately conservative in the safe direction: a real teammate whose
 * reference fails to resolve degrades to the system marker, which understates
 * coverage. The opposite default would let a drip campaign read as a colleague
 * having reached out, and a rep would skip the row.
 */
export function toLastCommunicationCell(communication?: LastCommunicationResult): LastCommunicationCell | undefined {
  if (!communication) {
    return undefined;
  }

  const { sent } = communication;
  const sender = communication.sender?.resource;

  if (isPractitioner(sender)) {
    const name = sender.name?.[0];

    // A service account is a Practitioner, but not a colleague (BAR-2864). It keeps
    // its name as the label so the marker still says which automation sent it.
    return isServiceAccount(sender)
      ? { sent, systemLabel: formatGivenFamilyName(name) || automatedLabel(communication) }
      : { sent, name };
  }

  return { sent, systemLabel: automatedLabel(communication) };
}

/** The Notes column: the patient's latest sticky note. */
export interface StickyNoteCell {
  /** Absent when the note is Restricted above the viewer's clearance. */
  text?: string;
  restricted: boolean;
  authorName?: HumanName;
  sent?: string;
  /** For the edit permission check and the update. */
  communication: Communication;
}

/**
 * A note above the viewer's confidentiality level comes back with `payload: null`
 * (the access policy's hidden field) and must read as restricted, never as empty.
 */
export function toStickyNoteCell(
  note: Communication | undefined,
  practitionerByRef: Map<string, Practitioner>
): StickyNoteCell | undefined {
  if (!note) {
    return undefined;
  }

  const authorRef = note.sender?.reference;
  return {
    text: getPayloadByType(note, TEXT_CONTENT_PAYLOAD_ID)?.contentString,
    restricted: note.payload === null,
    authorName: authorRef ? practitionerByRef.get(authorRef)?.name?.[0] : undefined,
    sent: note.sent,
    communication: note,
  };
}

/**
 * Every appointment hydration holds for the patient, so a row's lead status comes
 * from the same appointments the panel reads, not just the row's own.
 */
export interface PnConsultationCell {
  practitioner?: Practitioner;
  /** The consult's start. */
  date?: string;
}

/**
 * The PN consultation that started the coverage path (BAR-3094): the patient's latest PN
 * consultation that has occurred, and the PN on it. The path starts from that consult's card,
 * and when it started isn't stored, so the latest occurred one stands in.
 */
export function pnConsultationFor(
  hydration: Pick<PacQueueHydration, 'consultsByPatient' | 'practitionerByRef'>,
  patientRef: string,
  now: Date
): PnConsultationCell | undefined {
  // Newest first, as hydration sorts them.
  const consult = (hydration.consultsByPatient.get(patientRef) ?? []).find(
    (appointment) => isPnConsultationAppointment(appointment) && appointmentHasOccurred(appointment, now)
  );
  if (!consult) {
    return undefined;
  }
  const practitioner = (consult.participant ?? [])
    .map((participant) => participant.actor?.reference)
    .filter((reference): reference is string => Boolean(reference?.startsWith('Practitioner/')))
    .map((reference) => hydration.practitionerByRef.get(reference))
    .find((candidate): candidate is Practitioner => candidate !== undefined);
  return { practitioner, date: consult.start };
}

/** The payor, with what it covers under it: the employer under Carrum or Transcarent. */
export interface PayorCell {
  name?: string;
  detail?: string;
}

/**
 * The Coverage tabs' payor (BAR-3094): the administrator over the employer, or the insurer the
 * stage bot stored with the payment status, so no Organization is read.
 */
export function payorCellFor(
  track: Track | undefined,
  employer: string | undefined,
  paymentStatus: PaymentStatusResult | undefined
): PayorCell | undefined {
  if (track === Track.CARRUM || track === Track.TRANSCARENT) {
    return { name: TrackLabel[track], detail: employer };
  }
  if (track === Track.INSURANCE && paymentStatus?.payor) {
    return { name: paymentStatus.payor };
  }
  return undefined;
}

export function hydratedAppointments(
  hydration: Record<'consultByPatient' | 'introCallByPatient' | 'procedureByPatient', Map<string, Appointment>>,
  patientRef: string
): Appointment[] {
  return [
    hydration.consultByPatient.get(patientRef),
    hydration.introCallByPatient.get(patientRef),
    hydration.procedureByPatient.get(patientRef),
  ].filter((appointment): appointment is Appointment => appointment !== undefined);
}

export interface ActiveLeadForInput {
  /** From bulk hydration (`lastCommunicationByPatient`). */
  lastCommunicationSent?: string;
  /** Every live appointment known for this row — drives the upcoming-appointment rule. */
  appointments: ActiveLeadAppointment[];
  /** From bulk hydration; absent while it is still loading. */
  leadFlags?: Flag[];
  /** `EpisodeOfCare.period.start` from bulk hydration. */
  signupAt?: string;
  now: Date;
}

export function activeLeadFor({
  lastCommunicationSent,
  appointments,
  leadFlags,
  signupAt,
  now,
}: ActiveLeadForInput): ActiveLeadState {
  const override = (leadFlags ?? []).map(parseActiveLeadOverride).find(Boolean);

  return deriveActiveLead({
    now,
    lastCommunicationSent,
    appointments,
    signupAt,
    ...(override ? { override } : {}),
  });
}
